/**
 * When this device first saw each topic shared with the user — the Topics
 * "new" time of a share (review S7).
 *
 * The `chat-folder-shares` mirror carries no creation or share time, and its
 * `updatedAt` is rewritten by `mirror-folder-share.ts` on EVERY edit of the
 * owner's folder (rename, icon, recipients), so using it re-badged the topic
 * each time. First-seen never moves: a share is new once, when it shows up.
 *
 * - Kept per user and org in localStorage (the mirror listener is org-scoped).
 * - No stored map yet for this user and org (a fresh device, or the first run
 *   after this change): everything already shared is a baseline and never
 *   badges — the same "no flood" rule as NavSeenService's first run.
 * - Shares that disappear from the snapshot (unshared, deleted) are dropped.
 *
 * Mirrors written since crewdle-sdk-firebase `agent/topic-share-shared-at`
 * carry `sharedAt.{uid}` (set once per recipient); that wins over first-seen
 * — see {@link sharedTopicNewAt}. First-seen stays the fallback for older
 * mirrors (no backfill).
 */
const STORAGE_PREFIX = 'crwd.nav.shared-topics.';

/** `0` marks a baseline share: known, never "new". */
type FirstSeenMap = Record<string, number>;

export function sharedTopicKey(ownerUid: string, folderId: string): string {
  return `${ownerUid}__${folderId}`;
}

/**
 * The first-seen time (epoch ms) of each share key in `keys`, or undefined for
 * a baseline share. Persists the updated map.
 *
 * `fromCache`: a snapshot served from the local cache (often empty on a fresh
 * device) never sets the baseline — the server snapshot that follows would
 * otherwise make every existing share "new".
 */
export function stampSharedTopics(
  uid: string, orgId: string, keys: readonly string[], fromCache = false, now = Date.now(),
): Record<string, number | undefined> {
  const storageKey = `${STORAGE_PREFIX}${uid}.${orgId}`;
  const stored = read(storageKey);
  const baseline = stored === null;
  if (baseline && fromCache) return {};
  const next: FirstSeenMap = {};
  const out: Record<string, number | undefined> = {};
  for (const key of keys) {
    const ms = stored?.[key] ?? (baseline ? 0 : now);
    next[key] = ms;
    out[key] = ms > 0 ? ms : undefined;
  }
  write(storageKey, next);
  return out;
}

function read(storageKey: string): FirstSeenMap | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const map: FirstSeenMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      const ms = Number(value);
      if (Number.isFinite(ms) && ms >= 0) map[key] = ms;
    }
    return map;
  } catch {
    return null;
  }
}

function write(storageKey: string, map: FirstSeenMap): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    // Private mode / quota: shares then count as baseline next time (no badge).
  }
}

/**
 * The Topics "new" time of a share for `uid`: the mirror's `sharedAt.{uid}`
 * (server time this user was added; never moves on owner edits) when present,
 * else this device's first-seen time (undefined for a baseline share).
 */
export function sharedTopicNewAt(
  sharedAt: unknown, uid: string, firstSeenMs: number | undefined,
): number | undefined {
  const stamp = sharedAt && typeof sharedAt === 'object' ? (sharedAt as Record<string, unknown>)[uid] : undefined;
  const ms = (stamp as { toMillis?: () => number } | undefined)?.toMillis?.();
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : firstSeenMs;
}
